import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Avatar, Box, Card, Divider, Drawer, IconButton, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import MaleImage from "../../../assets/images/male.png";
import FemaleImage from "../../../assets/images/female.png";

function buildPersonMap(members) {
  const m = new Map();
  (members || []).forEach((p) => {
    if (!p) return;
    const idStr = p.id !== undefined && p.id !== null ? String(p.id) : null;
    if (idStr) {
      m.set(idStr, p);
      if (p.id !== idStr) {
        m.set(p.id, p);
      }
    }
  });
  return m;
}

const normalizeId = (value) => {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  if (!str || str.toLowerCase() === "null" || str.toLowerCase() === "undefined") return null;
  return str;
};

const getParentId = (person) => {
  if (!person) return null;
  return normalizeId(
    person.parentId ??
    person.parent_id ??
    person.parentid_c ??
    person.parentID ??
    person.parent_record_id
  );
};

const getSpouseId = (person) => {
  if (!person) return null;
  return normalizeId(
    person.spouseId ??
    person.spouse_id ??
    person.spouseid_c ??
    person.spouseID
  );
};

const getGender = (person) => {
  if (!person) return "";
  const raw = (person.gender ?? person.gender_c ?? "").toString().trim().toLowerCase();
  if (raw === "male" || raw === "female") return raw;
  if (raw === "m") return "male";
  if (raw === "f") return "female";
  return "male";
};

const getOrdinalLabel = (degree) => {
  const n = Math.abs(degree);
  if (n <= 0) return "";
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

const getRemovedLabel = (count) => {
  if (count <= 0) return "";
  if (count === 1) return "once removed";
  if (count === 2) return "twice removed";
  return `${count} times removed`;
};

const describeAncestorTerm = (steps) => {
  if (steps <= 0) return "";
  if (steps === 1) return "parent";
  if (steps === 2) return "grandparent";
  return `${"great-".repeat(steps - 2)}grandparent`;
};

const describeDescendantTerm = (steps) => {
  if (steps <= 0) return "";
  if (steps === 1) return "child";
  if (steps === 2) return "grandchild";
  return `${"great-".repeat(steps - 2)}grandchild`;
};

const describeRemovedCousinDetail = (source, target, sourceLevel, targetLevel, baseLabel) => {
  const diff = Math.abs(sourceLevel - targetLevel);
  if (diff === 0) return `${source.name} is ${baseLabel} of ${target.name}`;
  if (sourceLevel <= targetLevel) {
    const term = describeAncestorTerm(diff);
    return `${source.name} is ${baseLabel} of ${target.name}'s ${term}`;
  }
  const term = describeDescendantTerm(diff);
  return `${source.name} is ${baseLabel} of ${target.name}'s ${term}`;
};

const shareSameParents = (personA, personB) => {
  if (!personA || !personB) return false;
  const parentA = getParentId(personA);
  const parentB = getParentId(personB);
  return Boolean(parentA && parentB && parentA === parentB);
};

const buildGreatPrefix = (count) => {
  if (count <= 0) return "";
  return `${Array(count).fill("Great").join("-")}-`;
};

const buildExtendedUncleTerm = (gender, stepsFromParent) => {
  const prefix = buildGreatPrefix(Math.max(stepsFromParent, 0));
  const base = gender === "female" ? "Aunt" : "Uncle";
  return `${prefix}${base}`;
};

const buildExtendedNiblingTerm = (gender, stepsFromParent) => {
  const prefix = buildGreatPrefix(Math.max(stepsFromParent, 0));
  const base = gender === "female" ? "Niece" : "Nephew";
  return `${prefix}${base}`;
};

export default function RelationshipChecker({ 
  members, 
  isOpen, 
  onClose,
  onNodeClick,
  onPairSelected,
  currentPath,
}) {
  const persons = Array.isArray(members) ? members : [];
  const personMap = useMemo(() => buildPersonMap(persons), [persons]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const drawerAnchor = isMobile ? "bottom" : "right";
  const drawerPaperStyles = {
    width: isMobile ? "100vw" : 380,
    maxWidth: "100vw",
    // On mobile, keep the drawer shorter so it doesn't cover the whole screen and
    // allows clicking on the family tree behind it.
    height: isMobile ? "36vh" : "100vh",
    maxHeight: isMobile ? "44vh" : "100vh",
    padding: isMobile ? 2 : 3,
    display: "flex",
    flexDirection: "column",
    borderTopLeftRadius: isMobile ? 20 : 0,
    borderTopRightRadius: isMobile ? 20 : 0,
    pointerEvents: "auto",
    boxShadow: isMobile ? "0 -8px 28px rgba(15, 23, 42, 0.35)" : undefined,
  };

  const adjacency = useMemo(() => {
    const map = new Map();
    const addEdge = (rawA, rawB) => {
      const a = normalizeId(rawA);
      const b = normalizeId(rawB);
      if (!a || !b || a === b) return;
      if (!personMap.has(a) || !personMap.has(b)) return;
      if (!map.has(a)) map.set(a, new Set());
      if (!map.has(b)) map.set(b, new Set());
      map.get(a).add(b);
      map.get(b).add(a);
    };

    persons.forEach((person) => {
      if (!person) return;
      const selfId = normalizeId(person.id ?? person.record_id ?? person.personId);
      if (!selfId) return;

      const parentId = getParentId(person);
      if (parentId) addEdge(selfId, parentId);

      const spouseId = getSpouseId(person);
      if (spouseId) addEdge(selfId, spouseId);
    });

    return map;
  }, [persons, personMap]);

  const findPath = useCallback((startId, endId) => {
    if (!startId || !endId) return null;
    const start = String(startId);
    const end = String(endId);
    if (start === end) return null;
    const visited = new Set([start]);
    const queue = [start];
    const prev = new Map();

    while (queue.length) {
      const current = queue.shift();
      if (current === end) break;
      const neighbors = adjacency.get(current);
      if (!neighbors) continue;
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          prev.set(neighbor, current);
          queue.push(neighbor);
        }
      }
    }

    if (!visited.has(end)) return null;
    const path = [];
    let cursor = end;
    while (cursor !== undefined) {
      path.push(cursor);
      if (cursor === start) break;
      cursor = prev.get(cursor);
    }
    path.reverse();
    return path;
  }, [adjacency]);
  
  const [selectedPerson1, setSelectedPerson1] = useState(null);
  const [selectedPerson2, setSelectedPerson2] = useState(null);
  const [relationship, setRelationship] = useState(null);

  const handlePersonSelection = useCallback((person) => {
    if (!person) return;

    setRelationship(null);

    const clickedId = normalizeId(person.id);
    const firstId = selectedPerson1 ? normalizeId(selectedPerson1.id) : null;
    const secondId = selectedPerson2 ? normalizeId(selectedPerson2.id) : null;

    let nextFirst = selectedPerson1;
    let nextSecond = selectedPerson2;

    if (!nextFirst || firstId === clickedId) {
      nextFirst = person;
      if (secondId === clickedId) {
        nextSecond = null;
      }
    } else if (!nextSecond || secondId === clickedId) {
      nextSecond = person;
    } else {
      nextFirst = person;
      nextSecond = null;
    }

    setSelectedPerson1(nextFirst);
    setSelectedPerson2(nextSecond);
  }, [selectedPerson1, selectedPerson2]);

  useEffect(() => {
    if (typeof onNodeClick !== "function") return undefined;
    const handler = (person) => handlePersonSelection(person);
    onNodeClick(handler);
    return () => {
      onNodeClick(null);
    };
  }, [onNodeClick, handlePersonSelection]);

  const findParents = useCallback((person) => {
    const parentId = getParentId(person);
    if (!person || !parentId) return [];
    const parent1 = personMap.get(parentId);
    if (!parent1) return [];
    const spouseOfParent = getSpouseId(parent1);
    const parent2 = spouseOfParent ? personMap.get(spouseOfParent) || null : null;
    return parent2 ? [parent1, parent2] : [parent1];
  }, [personMap]);

  const calculateRelationship = useCallback((person1, person2, providedPath) => {
    if (!person1 || !person2) return { forward: "", reverse: "" };
    if (person1.id === person2.id) return { forward: "Same person", reverse: "" };

    let path = providedPath;
    if ((!path || !path.length) && person1 && person2) {
      path = findPath(person1.id, person2.id);
    }

    const person1Spouse = getSpouseId(person1);
    const person2Spouse = getSpouseId(person2);
    const person1Id = normalizeId(person1.id);
    const person2Id = normalizeId(person2.id);

    if ((person1Spouse && person1Spouse === person2Id) || (person2Spouse && person2Spouse === person1Id)) {
      const term1 = person1.gender === "male" ? "Husband" : "Wife";
      const term2 = person2.gender === "male" ? "Husband" : "Wife";
      return {
        forward: `${person1.name} is ${term1} of ${person2.name}`,
        reverse: `${person2.name} is ${term2} of ${person1.name}`,
      };
    }

    const isParentOf = (parentPerson, childPerson) => {
      if (!parentPerson || !childPerson) return false;
      const childParentId = getParentId(childPerson);
      if (!childParentId) return false;
      if (childParentId === normalizeId(parentPerson.id)) return true;
      const spouseId = getSpouseId(parentPerson);
      if (spouseId && childParentId === spouseId) return true;
      return false;
    };

    const getParents = (person) => findParents(person);

    const getChildrenOf = (person) => persons.filter((m) => isParentOf(person, m));
    const isDirectChildOf = (child, parent) => isParentOf(parent, child);

    const getAncestors = (person, maxLevel = 10) => {
      const ancestors = [];
      let currentLevel = [person];
      let level = 0;

      while (currentLevel.length > 0 && level < maxLevel) {
        const nextLevel = [];
        for (const p of currentLevel) {
          const parents = getParents(p);
          for (const parent of parents) {
            ancestors.push({ person: parent, level: level + 1 });
            nextLevel.push(parent);
          }
        }
        currentLevel = nextLevel;
        level++;
      }
      return ancestors;
    };

    const getDescendants = (person, maxLevel = 10) => {
      const descendants = [];
      let currentLevel = [person];
      let level = 0;

      while (currentLevel.length > 0 && level < maxLevel) {
        const nextLevel = [];
        for (const p of currentLevel) {
          const children = persons.filter((m) => isParentOf(p, m));
          for (const child of children) {
            descendants.push({ person: child, level: level + 1 });
            nextLevel.push(child);
          }
        }
        currentLevel = nextLevel;
        level++;
      }
      return descendants;
    };

    const p1ParentId = getParentId(person1);
    const p2ParentId = getParentId(person2);
    const siblingsByBlood = Boolean(p1ParentId && p2ParentId && p1ParentId === p2ParentId);

    {
      const p1Children = getChildrenOf(person1);
      const p2Children = getChildrenOf(person2);
      const spouseOfP2 = (() => {
        const id = getSpouseId(person2);
        return id ? personMap.get(id) || null : null;
      })();
      const spouseOfP1 = (() => {
        const id = getSpouseId(person1);
        return id ? personMap.get(id) || null : null;
      })();

      const p2IsDirectChildOfP1 = isDirectChildOf(person2, person1);
      const p1IsDirectChildOfP2 = isDirectChildOf(person1, person2);

      const p2IsSpouseOfChildOfP1 = p1Children.some((ch) => getSpouseId(ch) === person2Id);
      const p1IsSpouseOfChildOfP2 = p2Children.some((ch) => getSpouseId(ch) === person1Id);

      const spouseOfP2IsChildOfP1 = spouseOfP2 ? isDirectChildOf(spouseOfP2, person1) : false;
      const spouseOfP1IsChildOfP2 = spouseOfP1 ? isDirectChildOf(spouseOfP1, person2) : false;

      if (siblingsByBlood && p2IsSpouseOfChildOfP1) {
        const siblingTerm1 = person1.gender === "male" ? "Brother" : "Sister";
        const siblingTerm2 = person2.gender === "male" ? "Brother" : "Sister";
        const elderTerm = person1.gender === "male" ? "Father-in-law" : "Mother-in-law";
        const youngerTerm = person2.gender === "male" ? "Son-in-law" : "Daughter-in-law";
        return {
          forward: `${person1.name} is ${siblingTerm1} and ${elderTerm} of ${person2.name}`,
          reverse: `${person2.name} is ${siblingTerm2} and ${youngerTerm} of ${person1.name}`,
        };
      }

      if (siblingsByBlood && p1IsSpouseOfChildOfP2) {
        const siblingTerm1 = person1.gender === "male" ? "Brother" : "Sister";
        const siblingTerm2 = person2.gender === "male" ? "Brother" : "Sister";
        const elderTerm = person2.gender === "male" ? "Father-in-law" : "Mother-in-law";
        const youngerTerm = person1.gender === "male" ? "Son-in-law" : "Daughter-in-law";
        return {
          forward: `${person2.name} is ${siblingTerm2} and ${elderTerm} of ${person1.name}`,
          reverse: `${person1.name} is ${siblingTerm1} and ${youngerTerm} of ${person2.name}`,
        };
      }

      if (p2IsSpouseOfChildOfP1 && (!p2IsDirectChildOfP1 || spouseOfP2IsChildOfP1)) {
        const elderTerm = person1.gender === "male" ? "Father-in-law" : "Mother-in-law";
        const youngerTerm = person2.gender === "male" ? "Son-in-law" : "Daughter-in-law";
        return {
          forward: `${person1.name} is ${elderTerm} of ${person2.name}`,
          reverse: `${person2.name} is ${youngerTerm} of ${person1.name}`,
        };
      }

      if (p1IsSpouseOfChildOfP2 && (!p1IsDirectChildOfP2 || spouseOfP1IsChildOfP2)) {
        const elderTerm = person2.gender === "male" ? "Father-in-law" : "Mother-in-law";
        const youngerTerm = person1.gender === "male" ? "Son-in-law" : "Daughter-in-law";
        return {
          forward: `${person2.name} is ${elderTerm} of ${person1.name}`,
          reverse: `${person1.name} is ${youngerTerm} of ${person2.name}`,
        };
      }
    }

    if (isParentOf(person1, person2)) {
      const parentTerm = person1.gender === "male" ? "Father" : "Mother";
      const childTerm = person2.gender === "male" ? "Son" : "Daughter";
      return {
        forward: `${person1.name} is ${parentTerm} of ${person2.name}`,
        reverse: `${person2.name} is ${childTerm} of ${person1.name}`,
      };
    }
    if (isParentOf(person2, person1)) {
      const parentTerm = person2.gender === "male" ? "Father" : "Mother";
      const childTerm = person1.gender === "male" ? "Son" : "Daughter";
      return {
        forward: `${person2.name} is ${parentTerm} of ${person1.name}`,
        reverse: `${person1.name} is ${childTerm} of ${person2.name}`,
      };
    }

    if (siblingsByBlood) {
      const term1 = person1.gender === "male" ? "Brother" : "Sister";
      const term2 = person2.gender === "male" ? "Brother" : "Sister";
      return {
        forward: `${person1.name} is ${term1} of ${person2.name}`,
        reverse: `${person2.name} is ${term2} of ${person1.name}`,
      };
    }

    const person1Ancestors = getAncestors(person1);
    const person2Ancestors = getAncestors(person2);

    const p1AncestorOfP2 = person2Ancestors.find((a) => a.person.id === person1.id);
    // Detect ancestor-sibling relations (great-uncles/aunts & great-nephews/nieces)
    for (const anc of person1Ancestors) {
      if (anc.level <= 1) continue;
      if (shareSameParents(anc.person, person2)) {
        const stepsFromParent = anc.level - 1;
        const elderTerm = buildExtendedUncleTerm(person2.gender, stepsFromParent);
        const youngerTerm = buildExtendedNiblingTerm(person1.gender, stepsFromParent);
        return {
          forward: `${person2.name} is ${elderTerm} of ${person1.name}`,
          reverse: `${person1.name} is ${youngerTerm} of ${person2.name}`,
        };
      }
    }

    for (const anc of person2Ancestors) {
      if (anc.level <= 1) continue;
      if (shareSameParents(anc.person, person1)) {
        const stepsFromParent = anc.level - 1;
        const elderTerm = buildExtendedUncleTerm(person1.gender, stepsFromParent);
        const youngerTerm = buildExtendedNiblingTerm(person2.gender, stepsFromParent);
        return {
          forward: `${person1.name} is ${elderTerm} of ${person2.name}`,
          reverse: `${person2.name} is ${youngerTerm} of ${person1.name}`,
        };
      }
    }

    if (p1AncestorOfP2) {
      const level = p1AncestorOfP2.level;
      let ancestorTerm;
      let descendantTerm;

      if (level === 1) {
        ancestorTerm = person1.gender === "male" ? "Father" : "Mother";
        descendantTerm = person2.gender === "male" ? "Son" : "Daughter";
      } else if (level === 2) {
        ancestorTerm = person1.gender === "male" ? "Grandfather" : "Grandmother";
        descendantTerm = person2.gender === "male" ? "Grandson" : "Granddaughter";
      } else {
        const greats = "Great-".repeat(level - 2);
        ancestorTerm = person1.gender === "male" ? `${greats}Grandfather` : `${greats}Grandmother`;
        descendantTerm = person2.gender === "male" ? `${greats}Grandson` : `${greats}Granddaughter`;
      }

      return {
        forward: `${person1.name} is ${ancestorTerm} of ${person2.name}`,
        reverse: `${person2.name} is ${descendantTerm} of ${person1.name}`,
      };
    }

    const p2AncestorOfP1 = person1Ancestors.find((a) => a.person.id === person2.id);
    if (p2AncestorOfP1) {
      const level = p2AncestorOfP1.level;
      let ancestorTerm;
      let descendantTerm;

      if (level === 1) {
        ancestorTerm = person2.gender === "male" ? "Father" : "Mother";
        descendantTerm = person1.gender === "male" ? "Son" : "Daughter";
      } else if (level === 2) {
        ancestorTerm = person2.gender === "male" ? "Grandfather" : "Grandmother";
        descendantTerm = person1.gender === "male" ? "Grandson" : "Granddaughter";
      } else {
        const greats = "Great-".repeat(level - 2);
        ancestorTerm = person2.gender === "male" ? `${greats}Grandfather` : `${greats}Grandmother`;
        descendantTerm = person1.gender === "male" ? `${greats}Grandson` : `${greats}Granddaughter`;
      }

      return {
        forward: `${person2.name} is ${ancestorTerm} of ${person1.name}`,
        reverse: `${person1.name} is ${descendantTerm} of ${person2.name}`,
      };
    }

    for (const p1Ancestor of person1Ancestors) {
      for (const p2Ancestor of person2Ancestors) {
        if (p1Ancestor.person.id === p2Ancestor.person.id) {
          const levelDiff = Math.abs(p1Ancestor.level - p2Ancestor.level);
          const minLevel = Math.min(p1Ancestor.level, p2Ancestor.level);

          if (minLevel >= 2 && levelDiff === 0) {
            const degree = minLevel - 1;
            const ordinal = getOrdinalLabel(degree);
            const label = degree === 1 ? "1st Cousin" : `${ordinal} Cousin`;
            return {
              forward: `${person1.name} is ${label} of ${person2.name}`,
              reverse: `${person2.name} is ${label} of ${person1.name}`,
            };
          } else if (levelDiff === 1 && minLevel === 1) {
            const elder = p1Ancestor.level < p2Ancestor.level ? person1 : person2;
            const younger = p1Ancestor.level < p2Ancestor.level ? person2 : person1;
            const elderTerm = elder.gender === "male" ? "Uncle" : "Aunt";
            const youngerTerm = younger.gender === "male" ? "Nephew" : "Niece";

            if (elder.id === person1.id) {
              return {
                forward: `${person1.name} is ${elderTerm} of ${person2.name}`,
                reverse: `${person2.name} is ${youngerTerm} of ${person1.name}`,
              };
            }

            return {
              forward: `${person2.name} is ${elderTerm} of ${person1.name}`,
              reverse: `${person1.name} is ${youngerTerm} of ${person2.name}`,
            };
          } else if (minLevel >= 2) {
            const degree = minLevel - 1;
            const ordinal = getOrdinalLabel(degree);
            const baseLabel = degree === 1 ? "1st Cousin" : `${ordinal} Cousin`;
            const removedLabel = getRemovedLabel(levelDiff);
            const relationLabel = removedLabel ? `${baseLabel} ${removedLabel}` : baseLabel;
            const detailForward = describeRemovedCousinDetail(person1, person2, p1Ancestor.level, p2Ancestor.level, baseLabel);
            const detailReverse = describeRemovedCousinDetail(person2, person1, p2Ancestor.level, p1Ancestor.level, baseLabel);
            const forwardText = removedLabel ? `${detailForward} (${relationLabel})` : detailForward;
            const reverseText = removedLabel ? `${detailReverse} (${relationLabel})` : detailReverse;
            return {
              forward: forwardText,
              reverse: reverseText,
            };
          } else {
            const elderLevel = Math.max(p1Ancestor.level, p2Ancestor.level);
            const removed = levelDiff;
            return {
              forward: `${person1.name} and ${person2.name} are ${removed} times removed cousins`,
              reverse: `(${elderLevel - 1} generation difference)`
            };
          }
        }
      }
    }

    // Check for indirect in-law relationships through descendants
    const p1Descendants = getDescendants(person1);
    for (const desc of p1Descendants) {
      if (getSpouseId(desc.person) === person2Id) {
        const level = desc.level;
        let relTerm;
        if (level === 1) {
          relTerm = desc.person.gender === "male" ? "son" : "daughter";
        } else if (level === 2) {
          relTerm = desc.person.gender === "male" ? "grandson" : "granddaughter";
        } else {
          const greats = "great-".repeat(level - 2);
          relTerm = `${greats}grand${desc.person.gender === "male" ? "son" : "daughter"}`;
        }
        const person2Role = person2.gender === "male" ? "husband" : "wife";
        const spouseRole = person2.gender === "male" ? "wife" : "husband";
        let ancestorTerm;
        if (level === 1) {
          ancestorTerm = person1.gender === "male" ? "father" : "mother";
        } else if (level === 2) {
          ancestorTerm = person1.gender === "male" ? "grandfather" : "grandmother";
        } else {
          const greats = "great-".repeat(level - 2);
          ancestorTerm = `${greats}grand${person1.gender === "male" ? "father" : "mother"}`;
        }
        return {
          forward: `${person2.name} is the ${person2Role} of ${person1.name}'s ${relTerm} (${desc.person.name})`,
          reverse: `${person1.name} is the ${ancestorTerm} of ${person2.name}'s ${spouseRole} (${desc.person.name})`,
        };
      }
    }

    const p2Descendants = getDescendants(person2);
    for (const desc of p2Descendants) {
      if (getSpouseId(desc.person) === person1Id) {
        const level = desc.level;
        let relTerm;
        if (level === 1) {
          relTerm = desc.person.gender === "male" ? "son" : "daughter";
        } else if (level === 2) {
          relTerm = desc.person.gender === "male" ? "grandson" : "granddaughter";
        } else {
          const greats = "great-".repeat(level - 2);
          relTerm = `${greats}grand${desc.person.gender === "male" ? "son" : "daughter"}`;
        }
        const person1Role = person1.gender === "male" ? "husband" : "wife";
        const spouseRole = person1.gender === "male" ? "wife" : "husband";
        let ancestorTerm;
        if (level === 1) {
          ancestorTerm = person2.gender === "male" ? "father" : "mother";
        } else if (level === 2) {
          ancestorTerm = person2.gender === "male" ? "grandfather" : "grandmother";
        } else {
          const greats = "great-".repeat(level - 2);
          ancestorTerm = `${greats}grand${person2.gender === "male" ? "father" : "mother"}`;
        }
        return {
          forward: `${person1.name} is the ${person1Role} of ${person2.name}'s ${relTerm} (${desc.person.name})`,
          reverse: `${person2.name} is the ${ancestorTerm} of ${person1.name}'s ${spouseRole} (${desc.person.name})`,
        };
      }
    }

    // Check for in-law relationships through ancestors
    for (const p1Anc of person1Ancestors) {
      for (const p2Anc of person2Ancestors) {
        if (getSpouseId(p1Anc.person) === normalizeId(p2Anc.person.id)) {
          const level1 = p1Anc.level;
          const level2 = p2Anc.level;
          const spouse1 = p1Anc.person;
          const spouse2 = p2Anc.person;
          let rel1 = '';
          if (level1 === 1) rel1 = spouse1.gender === "male" ? "son" : "daughter";
          else if (level1 === 2) rel1 = spouse1.gender === "male" ? "grandson" : "granddaughter";
          else rel1 = `${"great-".repeat(level1 - 2)}grand${spouse1.gender === "male" ? "son" : "daughter"}`;
          let rel2 = '';
          if (level2 === 1) rel2 = spouse2.gender === "male" ? "son" : "daughter";
          else if (level2 === 2) rel2 = spouse2.gender === "male" ? "grandson" : "granddaughter";
          else rel2 = `${"great-".repeat(level2 - 2)}grand${spouse2.gender === "male" ? "son" : "daughter"}`;
          const spouseTerm = spouse1.gender === "male" ? "husband" : "wife";
          return {
            forward: `${person1.name} is ${spouse2.name}'s ${spouseTerm}'s ${rel1}`,
            reverse: `${person2.name} is ${spouse1.name}'s ${spouseTerm}'s ${rel2}`,
          };
        }
        if (getSpouseId(p2Anc.person) === normalizeId(p1Anc.person.id)) {
          const level1 = p1Anc.level;
          const level2 = p2Anc.level;
          const spouse1 = p1Anc.person;
          const spouse2 = p2Anc.person;
          let rel1 = '';
          if (level1 === 1) rel1 = spouse1.gender === "male" ? "son" : "daughter";
          else if (level1 === 2) rel1 = spouse1.gender === "male" ? "grandson" : "granddaughter";
          else rel1 = `${"great-".repeat(level1 - 2)}grand${spouse1.gender === "male" ? "son" : "daughter"}`;
          let rel2 = '';
          if (level2 === 1) rel2 = spouse2.gender === "male" ? "son" : "daughter";
          else if (level2 === 2) rel2 = spouse2.gender === "male" ? "grandson" : "granddaughter";
          else rel2 = `${"great-".repeat(level2 - 2)}grand${spouse2.gender === "male" ? "son" : "daughter"}`;
          const spouseTerm = spouse2.gender === "male" ? "husband" : "wife";
          return {
            forward: `${person1.name} is ${spouse2.name}'s ${spouseTerm}'s ${rel1}`,
            reverse: `${person2.name} is ${spouse1.name}'s ${spouseTerm}'s ${rel2}`,
          };
        }
      }
    }

    if (path && path.length > 1) {
      const nodes = path
        .map((id) => personMap.get(id))
        .filter(Boolean);

      const describeDirectStep = (source, target, isFinal) => {
        if (!source || !target) return null;
        const sourceId = normalizeId(source.id);
        const targetId = normalizeId(target.id);
        const sourceParent = getParentId(source);
        const targetParent = getParentId(target);
        const sourceSpouse = getSpouseId(source);
        const targetSpouse = getSpouseId(target);
        const targetName = target?.name || "";
        const makeSegment = (label) => ({ label, name: isFinal ? "" : targetName });

        if (
          (sourceSpouse && sourceSpouse === targetId) ||
          (targetSpouse && targetSpouse === sourceId)
        ) {
          return makeSegment(getGender(target) === "male" ? "husband" : "wife");
        }

        if (sourceParent && sourceParent === targetId) {
          return makeSegment(getGender(target) === "male" ? "father" : "mother");
        }

        if (targetParent && targetParent === sourceId) {
          return makeSegment(getGender(target) === "male" ? "son" : "daughter");
        }

        if (sourceSpouse && targetParent && targetParent === sourceSpouse) {
          return makeSegment(getGender(target) === "male" ? "stepson" : "stepdaughter");
        }

        if (targetSpouse && sourceParent && sourceParent === targetSpouse) {
          return makeSegment(getGender(target) === "male" ? "stepfather" : "stepmother");
        }

        return makeSegment("relative");
      };

      const describeChain = (chainNodes) => {
        if (chainNodes.length < 2) return [];
        const segments = [];
        let prev = chainNodes[0];
        let idx = 1;

        while (idx < chainNodes.length) {
          const curr = chainNodes[idx];
          const next = idx + 1 < chainNodes.length ? chainNodes[idx + 1] : null;
          const prevParent = getParentId(prev);
          const currId = normalizeId(curr?.id);
          const nextParent = next ? getParentId(next) : null;

          if (
            next &&
            prevParent &&
            currId &&
            prevParent === currId &&
            nextParent === currId
          ) {
            const siblingTerm = getGender(next) === "male" ? "brother" : "sister";
            segments.push({ label: siblingTerm, name: next?.name || "" });
            prev = next;
            idx += 2;
            continue;
          }

          const step = describeDirectStep(prev, curr, idx === chainNodes.length - 1);
          if (step) {
            segments.push(step);
          }
          prev = curr;
          idx += 1;
        }

        return segments
          .filter(Boolean)
          .map(({ label, name }) => (name ? `${label} (${name})` : label));
      };

      const forwardSegments = describeChain(nodes);
      const reverseSegments = describeChain([...nodes].reverse());
      if (forwardSegments.length) {
        return {
          forward: `${person2.name} is ${person1.name}'s ${forwardSegments.join("'s ")}`,
          reverse: `${person1.name} is ${person2.name}'s ${reverseSegments.join("'s ")}`,
        };
      }
    }
  }, [findParents, findPath, personMap, persons]);

  // Recalculate relationship when either person changes
  useEffect(() => {
    if (selectedPerson1 && selectedPerson2) {
      const rel = calculateRelationship(selectedPerson1, selectedPerson2, currentPath);
      setRelationship(rel);
      if (onPairSelected) onPairSelected(selectedPerson1, selectedPerson2);
    } else {
      if (onPairSelected) onPairSelected(null, null);
    }
  }, [selectedPerson1, selectedPerson2, calculateRelationship, onPairSelected]);

  const handleResetSelection = useCallback(() => {
    setSelectedPerson1(null);
    setSelectedPerson2(null);
    setRelationship(null);
    if (onPairSelected) onPairSelected(null, null);
  }, [onPairSelected]);

  // Reset when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedPerson1(null);
      setSelectedPerson2(null);
      setRelationship(null);
      if (onPairSelected) onPairSelected(null, null);
    }
  }, [isOpen, onPairSelected]);

  return (
    <Drawer
      anchor={drawerAnchor}
      open={isOpen}
      onClose={onClose}
      hideBackdrop={true}
      ModalProps={{
        keepMounted: true,
        disableBackdropClick: true,
      }}
      sx={{
        zIndex: 1300,
        pointerEvents: "none",
        '& .MuiDrawer-paper': {
          ...drawerPaperStyles,
          overflow: 'hidden',
          zIndex: 1300,
        },
      }}
    >
      <MDBox display="flex" flexDirection="column" height="100%">
        {/* Header - Fixed */}
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography variant="h5" fontWeight="medium">
            Check Relationship
          </MDTypography>
          <IconButton onClick={onClose} size="small">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>

        <Divider sx={{ mb: 2 }} />

        {/* Scrollable Content */}
        <MDBox sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pr: 1 }}>
          <MDTypography variant="body2" color="text" mb={2} sx={{ 
            lineHeight: 1.6,
            wordWrap: 'break-word',
            whiteSpace: 'normal'
          }}>
            Click on two family members to see their relationship
          </MDTypography>

          {/* Person 1 Selection */}
          <Card sx={{ mb: 2, p: 2, backgroundColor: selectedPerson1 ? "#e3f2fd" : "#f5f5f5" }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Person 1
              </MDTypography>
              {selectedPerson1 && (
                <IconButton 
                  size="small" 
                  onClick={() => {
                    setSelectedPerson1(null);
                    setRelationship(null);
                  }}
                  sx={{ padding: '4px' }}
                >
                  <Icon fontSize="small">edit</Icon>
                </IconButton>
              )}
            </MDBox>
            {selectedPerson1 ? (
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Avatar
                  src={selectedPerson1.photoUrl || (selectedPerson1.gender === "male" ? MaleImage : FemaleImage)}
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    flexShrink: 0,
                    border: `3px solid ${(!selectedPerson1.alivestatus_c && !selectedPerson1.aliveStatus || (selectedPerson1.alivestatus_c || selectedPerson1.aliveStatus || '').toLowerCase() === 'alive') ? '#4caf50' : '#f44336'}`
                  }}
                />
                <Box flex={1} sx={{ minWidth: 0 }}>
                  <MDTypography variant="button" fontWeight="medium" sx={{ 
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: 1
                  }}>
                    {selectedPerson1.name}
                  </MDTypography>
                  <MDBox sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0.5, fontSize: '0.75rem' }}>
                    <MDTypography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>Age/Gender:</strong> {(selectedPerson1.age_c || selectedPerson1.age || 'N/A')}/{(selectedPerson1.gender_c || selectedPerson1.gender || 'N/A')}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>Phone:</strong> {selectedPerson1.mobile || selectedPerson1.phone || 'N/A'}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>Email:</strong> {selectedPerson1.email || 'N/A'}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>Marital Status:</strong> {selectedPerson1.spouseId ? 'Married' : 'Single'}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>Spouse:</strong> {selectedPerson1.spouseId ? (personMap.get(selectedPerson1.spouseId)?.name || 'N/A') : 'N/A'}
                    </MDTypography>
                  </MDBox>
                </Box>
              </Box>
            ) : (
              <MDTypography variant="caption" color="text" fontStyle="italic">
                Click on a family member
              </MDTypography>
            )}
          </Card>

          {/* Person 2 Selection */}
          <Card sx={{ mb: 2, p: 2, backgroundColor: selectedPerson2 ? "#e3f2fd" : "#f5f5f5" }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Person 2
              </MDTypography>
              {selectedPerson2 && (
                <IconButton 
                  size="small" 
                  onClick={() => {
                    setSelectedPerson2(null);
                    setRelationship(null);
                  }}
                  sx={{ padding: '4px' }}
                >
                  <Icon fontSize="small">edit</Icon>
                </IconButton>
              )}
            </MDBox>
            {selectedPerson2 ? (
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Avatar
                  src={selectedPerson2.photoUrl || (selectedPerson2.gender === "male" ? MaleImage : FemaleImage)}
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    flexShrink: 0,
                    border: `3px solid ${(!selectedPerson2.alivestatus_c && !selectedPerson2.aliveStatus || (selectedPerson2.alivestatus_c || selectedPerson2.aliveStatus || '').toLowerCase() === 'alive') ? '#4caf50' : '#f44336'}`
                  }}
                />
                <Box flex={1} sx={{ minWidth: 0 }}>
                  <MDTypography variant="button" fontWeight="medium" sx={{ 
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: 1
                  }}>
                    {selectedPerson2.name}
                  </MDTypography>
                  <MDBox sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0.5, fontSize: '0.75rem' }}>
                    <MDTypography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>Age/Gender:</strong> {(selectedPerson2.age_c || selectedPerson2.age || 'N/A')}/{(selectedPerson2.gender_c || selectedPerson2.gender || 'N/A')}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>Phone:</strong> {selectedPerson2.mobile || selectedPerson2.phone || 'N/A'}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>Email:</strong> {selectedPerson2.email || 'N/A'}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>Marital Status:</strong> {selectedPerson2.spouseId ? 'Married' : 'Single'}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>Spouse:</strong> {selectedPerson2.spouseId ? (personMap.get(selectedPerson2.spouseId)?.name || 'N/A') : 'N/A'}
                    </MDTypography>
                  </MDBox>
                </Box>
              </Box>
            ) : (
              <MDTypography variant="caption" color="text" fontStyle="italic">
                Click on another family member
              </MDTypography>
            )}
          </Card>

          {/* Relationship Result */}
          {relationship && relationship.forward && (
            <Card sx={{ mb: 2, p: 2, backgroundColor: "#e8f5e9" }}>
              <MDTypography variant="caption" color="text" fontWeight="bold" mb={1} display="block">
                Relationship
              </MDTypography>
              <MDBox sx={{ width: '100%', overflow: 'hidden' }}>
                <MDTypography variant="body2" fontWeight="medium" color="success" sx={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.6,
                  mb: relationship.reverse ? 1.5 : 0,
                  display: 'block'
                }}>
                  {relationship.forward}
                </MDTypography>
                {relationship.reverse && (
                  <MDTypography variant="body2" fontWeight="medium" color="info" sx={{
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                    display: 'block'
                  }}>
                    {relationship.reverse}
                  </MDTypography>
                )}
              </MDBox>
            </Card>
          )}

          {/* Action Buttons */}
          <MDBox display="flex" gap={1} mt={2}>
            {(selectedPerson1 || selectedPerson2) && (
              <MDButton
                variant="outlined"
                color="error"
                fullWidth
                onClick={handleResetSelection}
              >
                <Icon sx={{ mr: 1 }}>refresh</Icon>
                Reset
              </MDButton>
            )}
            <MDButton
              variant="contained"
              color="dark"
              fullWidth
              onClick={onClose}
            >
              <Icon sx={{ mr: 1 }}>close</Icon>
              Close
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Drawer>
  );
}
